/**
 * GLAMRDiP Stories — Instagram-style video story viewer.
 * 2026-04-28 rewrite: image-slide pattern → video pattern per Joel.
 *
 * Page-speed contract (non-negotiable):
 *   - Story circles render thumbnails only (loading="lazy" via Liquid).
 *   - Modal <video> element is preload="none" with empty src on initial render.
 *   - Zero video bytes load until user clicks a story circle.
 *   - On open: video.src set, video.load(), video.play().
 *   - On navigate: previous video paused, src cleared via removeAttribute, load().
 *   - Single <video> element, src-swapped on navigate (NOT N video elements).
 *
 * UX:
 *   - Autoplay on open (mute respected from sessionStorage).
 *   - Auto-advance on `ended`. At last story → 1s pause then loop to first.
 *   - Tap-zones (left half = prev, right half = next).
 *   - Swipe (horizontal = prev/next, vertical-down = close).
 *   - Keyboard ←/→/Esc/Space/M.
 *   - Mute preference persists in sessionStorage.
 *
 * Accessibility:
 *   - role="dialog" aria-modal="true" aria-labelledby points at story title.
 *   - All buttons aria-labelled.
 *   - Focus returned to triggering circle on close.
 *   - prefers-reduced-motion: progress bar transition disabled via CSS.
 */
(() => {
  'use strict';

  const MUTE_KEY = 'gd-stories-muted';
  const END_PAUSE_MS = 1000;  // 1s pause at last story before looping (per Joel)

  class GDStories {
    constructor(roots) {
      // Each highlight row is a "set" — multiple sets on one page OK
      // (e.g. desktop variant + mobile variant of the same data).
      this.sets = [...roots].map((root) => {
        const dataEl = root.querySelector('[data-gd-stories-data]');
        let stories = [];
        try { stories = JSON.parse(dataEl?.textContent || '{"stories":[]}').stories || []; }
        catch { stories = []; }
        return { root, stories };
      }).filter((s) => s.stories.length > 0);
      if (!this.sets.length) return;

      this.currentSet = null;
      this.currentIdx = 0;
      this.muted = sessionStorage.getItem(MUTE_KEY) === '1';
      this.loopTimer = null;
      this._lastTrigger = null;

      this.modal = this._buildModal();
      this.video = this.modal.querySelector('[data-stories-video]');
      this.progressEl = this.modal.querySelector('[data-stories-progress]');
      this.titleEl = this.modal.querySelector('[data-stories-title]');
      this.thumbEl = this.modal.querySelector('[data-stories-thumb]');
      this.btnPause = this.modal.querySelector('[data-stories-pause]');
      this.btnMute = this.modal.querySelector('[data-stories-mute]');
      document.body.appendChild(this.modal);

      this._bind();
    }

    _buildModal() {
      const root = document.createElement('div');
      root.className = 'gd-stories';
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('aria-labelledby', 'gd-stories-title');
      root.hidden = true;
      // Single <video preload="none"> — no video bytes load until src is set.
      root.innerHTML = [
        '<button type="button" class="gd-stories__backdrop" data-stories-close aria-label="Close stories"></button>',
        '<button type="button" class="gd-stories__nav gd-stories__nav--prev" data-stories-prev aria-label="Previous story">',
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"/></svg>',
        '</button>',
        '<div class="gd-stories__player" data-stories-player>',
          '<video class="gd-stories__video" data-stories-video preload="none" playsinline></video>',
          '<div class="gd-stories__tap-zones">',
            '<button type="button" class="gd-stories__tap gd-stories__tap--prev" data-stories-prev aria-label="Previous story (tap)"></button>',
            '<button type="button" class="gd-stories__tap gd-stories__tap--next" data-stories-next aria-label="Next story (tap)"></button>',
          '</div>',
          '<div class="gd-stories__progress" data-stories-progress role="progressbar" aria-label="Story progress"></div>',
          '<div class="gd-stories__head">',
            '<div class="gd-stories__chip">',
              '<span class="gd-stories__thumb"><img data-stories-thumb alt="" loading="lazy"></span>',
              '<span class="gd-stories__title" id="gd-stories-title" data-stories-title></span>',
            '</div>',
            '<div class="gd-stories__controls">',
              '<button type="button" class="gd-stories__ctrl" data-stories-pause aria-label="Pause">',
                '<svg class="gd-stories__icon-pause" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>',
                '<svg class="gd-stories__icon-play" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20"/></svg>',
              '</button>',
              '<button type="button" class="gd-stories__ctrl" data-stories-mute aria-label="Mute">',
                '<svg class="gd-stories__icon-vol" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.5 9a4 4 0 0 1 0 6"/><path d="M19 6a8 8 0 0 1 0 12"/></svg>',
                '<svg class="gd-stories__icon-mute" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M11 5 6 9H2v6h4l5 4z"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>',
              '</button>',
              '<button type="button" class="gd-stories__ctrl" data-stories-close aria-label="Close">',
                '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>',
              '</button>',
            '</div>',
          '</div>',
        '</div>',
        '<button type="button" class="gd-stories__nav gd-stories__nav--next" data-stories-next aria-label="Next story">',
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>',
        '</button>',
      ].join('');
      return root;
    }

    _bind() {
      // Open via thumb click — event delegation per set root.
      this.sets.forEach((set) => {
        set.root.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-gd-story-open]');
          if (!trigger) return;
          e.preventDefault();
          const idx = parseInt(trigger.dataset.gdStoryOpen, 10) || 0;
          this.open(set, idx, trigger);
        });
      });

      // Close (backdrop + close buttons)
      this.modal.querySelectorAll('[data-stories-close]').forEach((b) =>
        b.addEventListener('click', (e) => { e.preventDefault(); this.close(); })
      );
      // Prev / Next (side arrows + tap-zones share these data-attrs)
      this.modal.querySelectorAll('[data-stories-prev]').forEach((b) =>
        b.addEventListener('click', (e) => { e.preventDefault(); this.prev(); })
      );
      this.modal.querySelectorAll('[data-stories-next]').forEach((b) =>
        b.addEventListener('click', (e) => { e.preventDefault(); this.next(); })
      );

      // Pause/play + mute/unmute
      this.btnPause?.addEventListener('click', (e) => { e.preventDefault(); this._togglePause(); });
      this.btnMute?.addEventListener('click', (e) => { e.preventDefault(); this._toggleMute(); });

      // Video events for progress + auto-advance
      this.video.addEventListener('timeupdate', () => this._renderProgress());
      this.video.addEventListener('ended', () => this._handleEnded());
      this.video.addEventListener('play', () => this._reflectState());
      this.video.addEventListener('pause', () => this._reflectState());
      this.video.addEventListener('loadedmetadata', () => this._renderProgress());

      // Keyboard
      document.addEventListener('keydown', (e) => {
        if (this.modal.hidden) return;
        switch (e.key) {
          case 'Escape':     e.preventDefault(); this.close(); break;
          case 'ArrowLeft':  e.preventDefault(); this.prev(); break;
          case 'ArrowRight': e.preventDefault(); this.next(); break;
          case ' ':          e.preventDefault(); this._togglePause(); break;
          case 'm': case 'M': e.preventDefault(); this._toggleMute(); break;
        }
      });

      // Touch swipe — horizontal = prev/next; swipe-down = close.
      const player = this.modal.querySelector('[data-stories-player]');
      let startX = null, startY = null, startedAt = 0;
      player?.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        startedAt = performance.now();
      }, { passive: true });
      player?.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? startX) - startX;
        const dy = (e.changedTouches[0]?.clientY ?? startY) - startY;
        const dt = performance.now() - startedAt;
        startX = startY = null;
        if (dt > 600) return;  // not a swipe
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) this.next(); else this.prev();
        } else if (dy > 80 && Math.abs(dy) > Math.abs(dx)) {
          this.close();
        }
      }, { passive: true });
    }

    // ── Public API ────────────────────────────────────────────────────────
    open(set, idx, trigger) {
      const wasOpen = !this.modal.hidden;
      this.currentSet = set;
      this.currentIdx = Math.max(0, Math.min(set.stories.length - 1, idx));
      this._lastTrigger = trigger || null;
      this._renderSegments();
      this.modal.hidden = false;
      // Shared scroll-lock manager — keeps body locked while any
      // overlay is open (stories modal can stack on top of bundle
      // builder for the salon-kit reels).
      if (!wasOpen) {
        if (window.gdScrollLock) window.gdScrollLock.lock(this.modal);
        else document.body.style.overflow = 'hidden';
      }
      this._loadCurrent({ autoplay: true });  // autoplay-on-open per Joel
      // Focus the close button for keyboard a11y
      this.modal.querySelector('.gd-stories__ctrl[data-stories-close]')?.focus?.();
    }

    close() {
      if (this.modal.hidden) return; // idempotent — already closed
      if (this.loopTimer) { clearTimeout(this.loopTimer); this.loopTimer = null; }
      this._cleanupVideo();
      this.modal.hidden = true;
      if (window.gdScrollLock) window.gdScrollLock.unlock(this.modal);
      else document.body.style.overflow = '';
      this.currentSet = null;
      // a11y: return focus to the trigger that opened the modal
      this._lastTrigger?.focus?.();
      this._lastTrigger = null;
    }

    next() {
      if (!this.currentSet) return;
      // Cancel any pending loop-pause if user manually advances.
      if (this.loopTimer) { clearTimeout(this.loopTimer); this.loopTimer = null; }
      const total = this.currentSet.stories.length;
      if (this.currentIdx >= total - 1) {
        // At last story — 1s pause before looping to first (per Joel UX nicety).
        this.video.pause();
        this.loopTimer = setTimeout(() => {
          this.loopTimer = null;
          if (!this.currentSet) return;  // user closed during pause
          this.currentIdx = 0;
          this._loadCurrent({ autoplay: true });
        }, END_PAUSE_MS);
        return;
      }
      this.currentIdx++;
      this._loadCurrent({ autoplay: true });
    }

    prev() {
      if (!this.currentSet) return;
      if (this.loopTimer) { clearTimeout(this.loopTimer); this.loopTimer = null; }
      if (this.currentIdx <= 0) {
        // Restart current story from the beginning.
        try { this.video.currentTime = 0; } catch {}
        this.video.play().catch(() => {});
        return;
      }
      this.currentIdx--;
      this._loadCurrent({ autoplay: true });
    }

    // ── Internals ─────────────────────────────────────────────────────────
    _loadCurrent({ autoplay }) {
      const story = this.currentSet?.stories[this.currentIdx];
      if (!story) return;
      // Free previous video memory + clear any pending fallback timer.
      this._cleanupVideo();
      // Render thumb chip + title
      if (this.thumbEl) {
        this.thumbEl.src = story.thumbnail || '';
        this.thumbEl.alt = story.alt_text || story.label || '';
      }
      if (this.titleEl) this.titleEl.textContent = story.label || '';
      // Configure video — mute respected from sessionStorage
      this.video.muted = this.muted;
      this.video.volume = this.muted ? 0 : 1;
      if (story.video_url) {
        this.video.poster = story.thumbnail || '';
        this.video.src = story.video_url;
        try { this.video.load(); } catch {}
        if (autoplay) {
          // Joel 2026-05-03 (desktop autoplay fix): some browsers reject
          // unmuted .play() even within a user-gesture click chain
          // (Chrome desktop's autoplay policy is stricter than mobile
          // Safari's). Fall back to muted on rejection — every modern
          // browser allows muted autoplay. User can re-enable sound via
          // the existing mute toggle. Persist the mute fallback to
          // sessionStorage so subsequent navigates in the same session
          // skip the round-trip rejection rather than retrying unmuted
          // each time.
          const playPromise = this.video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
              this.muted = true;
              this.video.muted = true;
              this.video.volume = 0;
              try { sessionStorage.setItem(MUTE_KEY, '1'); } catch {}
              this._reflectState();
              this.video.play().catch(() => {/* rare — media disabled at OS level */});
            });
          }
        }
      } else {
        // Graceful fallback when a story has no video URL (e.g. legacy block
        // pre-2026-04-28 rewrite, before Joel uploads a video). Show the
        // thumbnail as a poster filling the player; auto-advance after 5s
        // so the experience doesn't dead-end. Logged in NEEDS_JOEL.md.
        this.video.poster = story.thumbnail || '';
        if (autoplay) {
          if (this._fallbackTimer) clearTimeout(this._fallbackTimer);
          this._fallbackTimer = setTimeout(() => this.next(), 5000);
        }
      }
      this._renderSegments();
      this._reflectState();
    }

    _cleanupVideo() {
      if (this._fallbackTimer) { clearTimeout(this._fallbackTimer); this._fallbackTimer = null; }
      if (!this.video) return;
      try { this.video.pause(); } catch {}
      this.video.removeAttribute('src');
      try { this.video.load(); } catch {}
    }

    _renderSegments() {
      if (!this.progressEl || !this.currentSet) return;
      const total = this.currentSet.stories.length;
      const cur = this.currentIdx;
      // Build segments lazily once per story-set.
      if (this.progressEl.childElementCount !== total) {
        this.progressEl.innerHTML = '';
        for (let i = 0; i < total; i++) {
          const seg = document.createElement('span');
          seg.className = 'gd-stories__seg';
          const fill = document.createElement('span');
          fill.className = 'gd-stories__seg-fill';
          seg.appendChild(fill);
          this.progressEl.appendChild(seg);
        }
      }
      // Update fill state per segment.
      [...this.progressEl.children].forEach((seg, i) => {
        const fill = seg.firstElementChild;
        if (!fill) return;
        if (i < cur)      fill.style.transform = 'scaleX(1)';
        else if (i > cur) fill.style.transform = 'scaleX(0)';
        // i === cur is animated by _renderProgress() on timeupdate.
      });
      this.progressEl.setAttribute('aria-valuenow', String(cur + 1));
      this.progressEl.setAttribute('aria-valuemax', String(total));
    }

    _renderProgress() {
      if (!this.progressEl || !this.currentSet) return;
      const seg = this.progressEl.children[this.currentIdx];
      if (!seg) return;
      const fill = seg.firstElementChild;
      if (!fill) return;
      const dur = this.video.duration || 0;
      if (!dur || !isFinite(dur)) return;
      const pct = Math.min(1, (this.video.currentTime || 0) / dur);
      fill.style.transform = `scaleX(${pct.toFixed(3)})`;
    }

    _handleEnded() { this.next(); }

    _togglePause() {
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }

    _toggleMute() {
      this.muted = !this.muted;
      sessionStorage.setItem(MUTE_KEY, this.muted ? '1' : '0');
      this.video.muted = this.muted;
      this._reflectState();
    }

    _reflectState() {
      if (this.btnPause) {
        const paused = this.video.paused;
        this.btnPause.dataset.state = paused ? 'paused' : 'playing';
        this.btnPause.setAttribute('aria-label', paused ? 'Play' : 'Pause');
      }
      if (this.btnMute) {
        this.btnMute.dataset.state = this.muted ? 'muted' : 'unmuted';
        this.btnMute.setAttribute('aria-label', this.muted ? 'Unmute' : 'Mute');
      }
    }
  }

  function init() {
    const roots = document.querySelectorAll('[data-gd-story-highlights]');
    if (!roots.length) return;
    if (window.__gdStoriesInit) return;
    window.__gdStoriesInit = new GDStories(roots);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
